import type { Entity } from '@api/api/entity-schema';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useKgInsight } from '@terminal/react-query-hooks/insights/useKgInsight';
import { queryKeys, queryValues } from '@terminal/routes/query-params';
import { MarketCatalystWidget } from '@terminal/widgets/market-catalyst-drawer/market-catalyst-widget/MarketCatalystWidget';
import type { FC } from 'react';
import { CompanyCatalystWidget } from '../company-catalyst-widget/CompanyCatalystWidget';
import { CatalystWidget } from './CatalystWidget';
import { KgInsightSkeleton } from './components/kg-insight-content/KgInsightSkeleton';

interface CatalystControllerProps {
  navigateToKGInsight: () => void;
  expandCitations: boolean;
  entity?: Entity;
}

export const CatalystController: FC<CatalystControllerProps> = ({
  navigateToKGInsight,
  expandCitations,
  entity,
}) => {
  const { params } = useQueryParams();
  const insightId = params[queryKeys.insightId] ?? '';
  const activeDrawer = params[queryKeys.activeDrawer];
  const isCompanyCatalystDrawer =
    activeDrawer === queryValues.catalystInsightsDrawer;
  const isMarketCatalystDrawer =
    activeDrawer === queryValues.marketCatalystDrawer;
  const { data: kgInsight, isLoading } = useKgInsight(insightId);

  if (isLoading) {
    return <KgInsightSkeleton />;
  }

  // Migration: no KG insight → legacy company/market catalyst widget.
  // If the legacy widget also fails, ErrorBoundaryWrapper shows the error.
  if (!kgInsight) {
    if (!insightId) {
      return null;
    }

    if (isCompanyCatalystDrawer) {
      return (
        <ErrorBoundaryWrapper variant="error">
          <CompanyCatalystWidget
            insightId={insightId}
            expandCitations={expandCitations}
          />
        </ErrorBoundaryWrapper>
      );
    }

    if (isMarketCatalystDrawer) {
      return (
        <ErrorBoundaryWrapper variant="error">
          <MarketCatalystWidget
            insightId={insightId}
            expandCitations={expandCitations}
          />
        </ErrorBoundaryWrapper>
      );
    }

    return null;
  }

  return (
    <CatalystWidget entity={entity} navigateToKGInsight={navigateToKGInsight} />
  );
};
